import React from 'react';
import {

  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Category as CategoryIcon } from '@mui/icons-material';
import { Ramo, CreateRamoDTO } from '../types/ramo';
import { ramoService } from '../services/ramoService';
import MainLayout from '../layouts/MainLayout';
import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEffect, useState } from 'react';

const GerenciarRamos: React.FC = () => {
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [ramoAtual, setRamoAtual] = useState<Ramo | null>(null);

  const {
    register,
    handleSubmit: handleSubmitForm,
    reset,
    formState: { errors },
  } = useForm<CreateRamoDTO>({
    defaultValues: {
      nome: '',
      descricao: '',
    },
  });

  const carregarRamos = async () => {
    try {
      const data = await ramoService.listarRamos();
      setRamos(data);
    } catch (error) {
      toast.error('Erro ao carregar ramos.');
      console.error('Erro ao carregar ramos:', error);
    }
  };

  useEffect(() => {
    carregarRamos();
  }, []);

  const handleOpenDialog = (ramo?: Ramo) => {
    if (ramo) {
      setRamoAtual(ramo);
      reset({
        nome: ramo.nome,
        descricao: ramo.descricao,
      });
    } else {
      setRamoAtual(null);
      reset({
        nome: '',
        descricao: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset({
      nome: '',
      descricao: '',
    });
  };

  const onSubmit = async (formData: CreateRamoDTO) => {
    try {
      if (ramoAtual) {
        await ramoService.atualizarRamo(ramoAtual.id, formData);
        toast.success('Ramo atualizado com sucesso!');
      } else {
        await ramoService.criarRamo(formData);
        toast.success('Ramo criado com sucesso!');
      }
      handleCloseDialog();
      carregarRamos();
    } catch (error) {
      toast.error('Erro ao salvar ramo.');
      console.error('Erro ao salvar ramo:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este ramo?')) {
      try {
        await ramoService.excluirRamo(id);
        toast.success('Ramo excluído com sucesso!');
        carregarRamos();
      } catch (error) {
        toast.error('Erro ao excluir ramo.');
        console.error('Erro ao excluir ramo:', error);
      }
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" color="primary.main" sx={{ fontWeight: 'bold' }}>
            <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2em' }} />
            Gerenciamento de Ramos
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Novo Ramo
          </Button>
        </Box>

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', textTransform: 'uppercase', bgcolor: 'grey.100' } }}>
                  <TableCell>Nome</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ramos.map((ramo) => (
                  <TableRow key={ramo.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>{ramo.nome}</TableCell>
                    <TableCell>{ramo.descricao}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Editar">
                          <IconButton color="primary" onClick={() => handleOpenDialog(ramo)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton color="error" onClick={() => handleDelete(ramo.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth component="form" onSubmit={handleSubmitForm(onSubmit)}>
        <DialogTitle>
          {ramoAtual ? 'Editar Ramo' : 'Novo Ramo'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>Dados do Ramo</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nome"
              fullWidth
              {...register('nome', { required: 'Nome é obrigatório' })}
              error={!!errors.nome}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.target.value = e.target.value.toUpperCase();
              }}
              helperText={errors.nome?.message}
            />
            <TextField
              label="Descrição"
              {...register('descricao')}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button type="submit" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default GerenciarRamos; 